import {
  createBankTitleMap,
  summarizeProductBanks,
  summarizeBookProjectBanks,
  getProjectCompanyCodeStatus,
  normalizeProjectCompanyCodes,
  flattenProductBankAssignments,
} from 'utils';
import {
  Box,
  Card,
  Chip,
  Stack,
  alpha,
  Button,
  Divider,
  useTheme,
  Typography,
  IconButton,
  DialogTitle,
  CardContent,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import BankUsageChip from 'components/bank-usage-chip';
import { FormattedMessage, useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import ResponsiveDialog from 'components/responsive-dialog';
import { BookProject, Project, UsedBank } from 'library/types';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BankStatusChip, { BankStatusSummary } from 'components/bank-status-chip';
import ProductCompanyStatusChip, { getProductCompanyStatusVisual } from 'components/product-company-status-chip';

type ShowProductBanksProps = {
  open: boolean;
  handleClose: () => void;
  projects: BookProject[];
  allProducts: Project[];
  usedBanks: UsedBank[];
  bookTitle?: string;
};

const ShowProductBanks = ({
  open,
  handleClose,
  projects = [],
  allProducts = [],
  usedBanks = [],
  bookTitle,
}: ShowProductBanksProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const bankTitleMap = useMemo(() => createBankTitleMap(usedBanks), [usedBanks]);
  const overallSummary = useMemo(() => summarizeBookProjectBanks(projects), [projects]);
  const projectsToDisplay = useMemo(
    () =>
      projects.filter((project) => {
        const hasAssignments = summarizeProductBanks(project.banks).assignmentCount > 0;
        return hasAssignments || Number(project.bankCount) > 0 || projects.length === 1;
      }),
    [projects]
  );

  return (
    <ResponsiveDialog open={open} onClose={handleClose} maxWidth="md" aria-labelledby="product-bank-details-title">
      <DialogTitle
        id="product-bank-details-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, pb: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
            }}>
            <AccountBalanceIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              <FormattedMessage id="report.bankDistribution" defaultMessage="Ürün Bazlı Banka Dağılımı" />
            </Typography>
            {bookTitle && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {bookTitle}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          aria-label={intl.formatMessage({ id: 'autocomplete.closeText', defaultMessage: 'Kapat' })}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems={{ sm: 'center' }}>
          <Chip
            label={intl.formatMessage(
              { id: 'report.bankAssignmentCount', defaultMessage: '{count} banka ataması' },
              { count: overallSummary.assignmentCount }
            )}
            color={overallSummary.assignmentCount > 0 ? 'primary' : 'default'}
            variant={overallSummary.assignmentCount > 0 ? 'filled' : 'outlined'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
          <Chip
            label={intl.formatMessage(
              { id: 'report.uniqueBankCount', defaultMessage: '{count} farklı banka' },
              { count: overallSummary.uniqueBankCount }
            )}
            variant="outlined"
            size="small"
          />
          <Box sx={{ ml: { sm: 'auto' } }}>
            <BankStatusSummary counts={overallSummary.statusCounts} />
          </Box>
        </Stack>

        {projectsToDisplay.length === 0 ? (
          <Box py={5} textAlign="center">
            <AccountBalanceIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              <FormattedMessage id="project.table.noBanks" defaultMessage="Henüz banka eklenmemiş" />
            </Typography>
          </Box>
        ) : (
          projectsToDisplay.map((project) => {
            const product = allProducts.find((item) => item.id === project.id);
            const productTitle =
              product?.title ??
              `${intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' })} #${project.id}`;
            const summary = summarizeProductBanks(project.banks);
            const groupsWithAssignments = (Array.isArray(project.banks) ? project.banks : []).filter(
              (group) => flattenProductBankAssignments([group]).length > 0
            );

            return (
              <Card key={project.id} variant="outlined" sx={{ borderRadius: 2, overflow: 'visible' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}>
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary.main" noWrap>
                        {productTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <FormattedMessage
                          id="report.productBankContext"
                          defaultMessage="{companyCount} şirket kodunda {uniqueBankCount} farklı banka"
                          values={{
                            companyCount:
                              normalizeProjectCompanyCodes(project.companyCodes).length || summary.assignedCompanyCount,
                            uniqueBankCount: summary.uniqueBankCount,
                          }}
                        />
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                      <BankStatusSummary counts={summary.statusCounts} />
                      <BankUsageChip
                        assignmentCount={summary.assignmentCount}
                        bankLimitUsageCount={summary.bankLimitUsageCount}
                        bankLimit={project.bankCount}
                      />
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {groupsWithAssignments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={1}>
                      <FormattedMessage
                        id="report.noProductBankAssignments"
                        defaultMessage="Bu ürün için henüz banka ataması yok."
                      />
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {groupsWithAssignments.map((group) => {
                        const assignments = flattenProductBankAssignments([group]);
                        const companyStatus = getProjectCompanyCodeStatus(project.companyCodes, group.companyCode);
                        const companyStatusVisual =
                          companyStatus === undefined ? null : getProductCompanyStatusVisual(companyStatus, theme);

                        return (
                          <Box
                            key={`${project.id}-${group.companyCode}`}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              overflow: 'hidden',
                            }}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{
                                px: 1.5,
                                py: 1,
                                bgcolor: companyStatusVisual?.backgroundColor,
                              }}>
                              <BusinessIcon
                                sx={{
                                  fontSize: 18,
                                  color: companyStatusVisual?.color ?? 'text.disabled',
                                }}
                              />
                              <Typography variant="body2" fontWeight={700} sx={{ flexGrow: 1 }}>
                                {group.companyCode}
                              </Typography>
                              {companyStatus !== undefined && (
                                <ProductCompanyStatusChip
                                  status={companyStatus}
                                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                                />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                <FormattedMessage
                                  id="report.bankAssignmentCount"
                                  defaultMessage="{count} banka ataması"
                                  values={{ count: assignments.length }}
                                />
                              </Typography>
                            </Stack>

                            <Divider />

                            <Stack divider={<Divider flexItem />}>
                              {assignments.map((assignment, index) => (
                                <Stack
                                  key={`${group.companyCode}-${assignment.bankId}-${index}`}
                                  direction="row"
                                  spacing={1.5}
                                  alignItems="center"
                                  justifyContent="space-between"
                                  sx={{ px: 1.5, py: 1.1 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                                    <AccountBalanceIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                      {bankTitleMap.get(assignment.bankId) ??
                                        `${intl.formatMessage({ id: 'project.table.bank', defaultMessage: 'Banka' })} #${assignment.bankId}`}
                                    </Typography>
                                  </Stack>
                                  <BankStatusChip status={assignment.status} />
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} variant="contained" size="small">
          <FormattedMessage id="dashboard.okButtonLabel" defaultMessage="Tamam" />
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default ShowProductBanks;
